import React, { memo } from "react";
import "./TableStructure.css";
import { CircularProgress } from "@mui/material";

const clockIcon = (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const printerIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9"/>
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
    <rect x="6" y="14" width="12" height="8"/>
  </svg>
);

const cardIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
    <line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);

const personIcon = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const TableStructure = ({
  name,
  seat,
  status,
  cartStatus,
  tableSelectionLoader,
  totalCartValue,
  elapsedMinutes,
  reservedTime,
}) => {
  const isOccupied = status === "OCCUPIED";
  const isReserved = status === "RESERVED";

  // state: available | created | occupied | payment | reserved
  let state = "available";
  if (isOccupied) {
    if (cartStatus === "ORDER_CREATED" || cartStatus === "PAYMENT_PENDING") {
      state = "payment";
    } else if (cartStatus === "ORDER_PLACED") {
      state = "occupied";
    } else {
      state = "created";
    }
  } else if (isReserved) {
    state = "reserved";
  }

  const statusLabel =
    state === "payment" ? "PAYMENT" :
    state === "occupied" ? "OCCUPIED" :
    state === "created" ? "OCCUPIED" : "";

  return (
    <div className={`ts-card ts-card--${state}`}>
      {tableSelectionLoader ? (
        <div className="ts-loader">
          <CircularProgress size={16} sx={{ color: "currentColor" }} />
        </div>
      ) : (
        <>
          {/* ── Row 1: name + time badge ── */}
          <div className="ts-top">
            <span className="ts-name">{name}</span>
            {(state === "occupied" || state === "created") && elapsedMinutes != null && (
              <span className="ts-time-badge ts-time-badge--occupied">
                {clockIcon}&nbsp;{elapsedMinutes}
              </span>
            )}
            {state === "payment" && elapsedMinutes != null && (
              <span className="ts-time-badge ts-time-badge--payment">{elapsedMinutes}</span>
            )}
            {state === "reserved" && reservedTime && (
              <span className="ts-reserved-time">{reservedTime}</span>
            )}
          </div>

          {/* ── Row 2: pax + status label ── */}
          {(state === "occupied" || state === "payment" || state === "created") && (
            <div className="ts-middle">
              <div className="ts-pax">
                {personIcon}
                <span>{seat}</span>
              </div>
              <span className="ts-status-label">{statusLabel}</span>
            </div>
          )}
          {state === "reserved" && (
            <div className="ts-middle">
              <div className="ts-pax">
                {personIcon}
                <span>{seat}</span>
              </div>
            </div>
          )}

          <div className="ts-spacer" />

          {/* ── Row 3: amount row (icons injected from outside via .ts-icons-slot) ── */}
          {(state === "occupied" || state === "payment" || state === "created") && (
            <div className="ts-bottom">
              <span className="ts-amount">
                {totalCartValue > 0 ? `\u20B9${totalCartValue?.toLocaleString("en-IN")}` : ""}
              </span>
              {/* Buttons are rendered by the parent and injected via absolute positioning */}
              <div className="ts-icons-slot" />
            </div>
          )}
          {state === "available" && (
            <div className="ts-bottom">
              <div className="ts-seats-row">
                {personIcon}
                <span>{seat} Seats</span>
              </div>
            </div>
          )}
          {state === "reserved" && (
            <div className="ts-bottom">
              <div className="ts-seats-row">
                {personIcon}
                <span>{seat} Seats</span>
              </div>
              <span className="ts-reserved-label">RESERVED</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default memo(TableStructure);
